import { FixedIncomeTypeNames } from "../../common/enums/AssetClassType";
import { TradeRestriction } from "../../trading/models/tradeRestriction";

export class TradeSimulation {
  portfolioId: number;
  portfolioName: string;
  portfolioCurrency: string;
  portfolioSource: string;
  cashBalance: number;
  currencyBalances: {
    currency: string;
    totalCashBalance: number;
    totalCashMURBalance: number;
  }[];

  quantity: number;
  targetQuantity: number;

  price: number;
  targetPrice: number;
  costPrice: number;

  tradeValue: number = 0;
  marketValue: number;
  targetMarketValue: number;
  idealMarketValue: number;

  portfolioPercentage: number;
  targetPortfolioPercentage: number;
  idealPortfolioPercentage: number;

  assetClassPercentage: number;
  targetAssetClassPercentage: number;
  idealAssetClassPercentage: number;

  cModel: any;
  cModelPercentage: any;
  idealBuySell: number;
  buySellQuantity: number = 0;
  portfolioNAV: number;
  assetClassNav: number;

  cashLimits: { highLimit: number; lowLimit: number };
  assetLimits: { assetClass: string; highLimit: number; lowLimit: number };
  hasActiveTradeSet: boolean;
  restriction: TradeRestriction;
  sellAllowed: boolean;
  assetClass: string;

  private primaryCashAccountBalance: {
    currency: string;
    totalCashBalance: number;
    totalCashMURBalance: number;
  };

  get cashAvailable() {
    return this.cashBalance - this.tradeValue;
  }

  get cModelDelta() {
    return this.targetAssetClassPercentage - this.cModelPercentage;
  }

  get withinCashLimits() {
    if (!this.cashLimits) {
      return true;
    }

    const upperLimit = this.portfolioNAV * (this.cashLimits.highLimit / 100);
    const lowerLimit = this.portfolioNAV * (this.cashLimits.lowLimit / 100);

    return this.cashAvailable <= upperLimit && this.cashAvailable >= lowerLimit;
  }

  get targetAssetClassNav() {
    return this.assetClassNav - this.marketValue + this.targetMarketValue;
  }

  get withinAssetLimits() {
    if (!this.assetLimits) {
      return true;
    }

    const upperLimit = this.portfolioNAV * (this.assetLimits.highLimit / 100);
    const lowerLimit = this.portfolioNAV * (this.assetLimits.lowLimit / 100);

    const newAssetClassNav = this.targetAssetClassNav;

    return newAssetClassNav <= upperLimit && newAssetClassNav >= lowerLimit;
  }

  get withinRestriction(): boolean {
    if (!this.restriction) {
      return true;
    }

    if (this.restriction.enabled === false) {
      return true;
    }

    if (!this.restriction.percentageOfPortfolio) {
      return true;
    }
    if (this.restriction.assetClass) {
      if (this.restriction.assetClass.name != this.assetClass) {
        return true;
      }
    }

    const targetPercentage =
      (this.targetQuantity * this.targetPrice) / this.portfolioNAV;
    return (
      this.restriction.maxPortfolio >= targetPercentage &&
      this.restriction.minPortfolio <= targetPercentage
    );
  }

  get exceedSecurityCashAccount(): boolean {
    if (!this.primaryCashAccountBalance) {
      return true;
    }
    return (
      this.primaryCashAccountBalance.totalCashBalance - this.tradeValue < 0
    );
  }

  constructor(portfolioSimulation, assetClass: string = "Unknown") {
    this.portfolioId = portfolioSimulation.portfolioId;
    this.portfolioName = portfolioSimulation.portfolioName;
    this.portfolioCurrency = portfolioSimulation.portfolioCurrency;
    this.portfolioSource = portfolioSimulation.portfolioSource;

    this.cashBalance = portfolioSimulation.cashBalance;
    this.currencyBalances = portfolioSimulation.currencyBalances;
    this.quantity = portfolioSimulation.quantity;

    this.primaryCashAccountBalance = this.currencyBalances.find(
      (c) => c.currency === this.portfolioCurrency
    );

    this.price = portfolioSimulation.price;
    this.targetPrice = portfolioSimulation.targetPrice;
    this.costPrice = portfolioSimulation.costPrice;

    this.marketValue = portfolioSimulation.marketValue;
    this.idealMarketValue =
      portfolioSimulation.targetPrice *
      (portfolioSimulation.idealBuySell + portfolioSimulation.quantity);
    this.assetClass = assetClass;
    if (assetClass == FixedIncomeTypeNames.BOND) {
      this.idealMarketValue = this.idealMarketValue / 100;
    }

    this.assetClassNav = portfolioSimulation.assetClassNav;
    this.assetClassPercentage = portfolioSimulation.assetClassPercentage;
    this.idealAssetClassPercentage =
      portfolioSimulation.idealAssetClassPercentage;

    this.portfolioNAV = portfolioSimulation.portfolioNav;
    this.portfolioPercentage = portfolioSimulation.portfolioPercentage;
    this.idealPortfolioPercentage =
      portfolioSimulation.idealPortfolioPercentage;

    this.cModel = portfolioSimulation.cModel;
    this.cModelPercentage = portfolioSimulation.cModel
      ? portfolioSimulation.cModel.modelPercentage
      : 0;
    this.cashLimits = portfolioSimulation.cashLimits;
    this.hasActiveTradeSet = portfolioSimulation.hasActiveTradeSet;

    this.idealBuySell = portfolioSimulation.idealBuySell;

    this.targetQuantity = portfolioSimulation.quantity + this.buySellQuantity;
    this.targetMarketValue =
      this.targetQuantity * portfolioSimulation.targetPrice;
    if (assetClass == FixedIncomeTypeNames.BOND) {
      this.targetMarketValue = this.targetMarketValue / 100;
    }
    this.targetPortfolioPercentage =
      (this.targetMarketValue * 100) / this.portfolioNAV;
    this.targetAssetClassPercentage =
      (this.targetMarketValue * 100) / this.assetClassNav;

    if (portfolioSimulation.tradeRestriction) {
      this.restriction = new TradeRestriction(
        portfolioSimulation.tradeRestriction
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/simulations/models/tradeSimulation.ts